import { useCallback, useEffect, useMemo, useState } from "react";
import type { User } from "oidc-client-ts";

import { api, ApiError } from "./api";
import { userManager } from "./auth";
import { bootstrapHandoff, enrollmentRequest, type EnrollmentDraft } from "./enrollment";
import {
  canEnroll,
  canExport,
  canReadAudit,
  canRevoke,
  canRevokeEmail,
  isSuperadmin,
  rolesFromProfile,
} from "./roles";
import type {
  AssignableAdministrator,
  AuditEvent,
  DeviceDetail,
  DeviceSummary,
  EnrollmentCreated,
  EnrollmentSummary,
  FleetReport,
  MonitoringEventsResponse,
  RegistrationSummary,
} from "./types";

type View = "dashboard" | "fleet" | "enrollments" | "monitoring" | "reports" | "audit";

const emptyEnrollment: EnrollmentDraft = {
  displayName: "",
  assetId: "",
  site: "",
  ownerTeam: "",
  tags: "",
  notes: "",
};

function ConfirmationPage() {
  const proof = useMemo(() => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "", []);
  const [details, setDetails] = useState<{ state: string; masked_email: string; expires_at: string } | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "confirmed" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!proof) {
      setMessage("This confirmation link is incomplete.");
      setStatus("error");
      return;
    }
    api.inspectConfirmation(proof)
      .then((value) => {
        setDetails(value);
        setStatus(value.state === "VERIFIED" ? "confirmed" : "ready");
      })
      .catch((error: ApiError) => {
        setMessage(error.message);
        setStatus("error");
      });
  }, [proof]);

  const confirm = async () => {
    setStatus("loading");
    try {
      await api.confirmEmail(proof);
      setStatus("confirmed");
      window.history.replaceState({}, "", "/confirm");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Confirmation failed");
      setStatus("error");
    }
  };

  return (
    <main className="confirmation-shell">
      <section className="confirmation-card">
        <div className="brand-mark" aria-hidden="true">BT</div>
        <p className="eyebrow">BotTimo enrollment</p>
        <h1>{status === "confirmed" ? "Email confirmed" : "Confirm your email"}</h1>
        {status === "loading" && <p className="muted">Checking this invitation…</p>}
        {status === "ready" && details && (
          <>
            <p>This Raspberry requested notifications for <strong>{details.masked_email}</strong>.</p>
            <p className="muted">Invitation expires {new Date(details.expires_at).toLocaleString()}.</p>
            <button className="primary" onClick={confirm}>Confirm email</button>
          </>
        )}
        {status === "confirmed" && <p>You can close this page. The Raspberry will detect verification on its next API call.</p>}
        {status === "error" && <div className="error-banner">{message}</div>}
        <p className="security-note">Opening the link did not confirm anything. Confirmation requires the button above.</p>
      </section>
    </main>
  );
}

function Login() {
  return (
    <main className="login-shell">
      <section className="login-copy">
        <p className="eyebrow">Methodius control plane</p>
        <h1>Small fleet.<br />Clear signals.</h1>
        <p>Enroll Raspberry systems, watch delivery health, and keep every privileged action accountable.</p>
        <button className="primary" onClick={() => userManager.signinRedirect()}>Sign in with Keycloak</button>
      </section>
      <section className="signal-panel" aria-label="Product overview">
        <div className="pulse pulse-one" />
        <div className="pulse pulse-two" />
        <div className="device-orbit"><span>3</span><small>planned devices</small></div>
      </section>
    </main>
  );
}

function Admin({ user }: { user: User }) {
  const [view, setView] = useState<View>("dashboard");
  const [report, setReport] = useState<FleetReport | null>(null);
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [enrollments, setEnrollments] = useState<EnrollmentSummary[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [error, setError] = useState("");
  const [enrollmentDraft, setEnrollmentDraft] = useState<EnrollmentDraft>(emptyEnrollment);
  const [created, setCreated] = useState<EnrollmentCreated | null>(null);
  const [creating, setCreating] = useState(false);
  const [cancellationTarget, setCancellationTarget] = useState<EnrollmentSummary | null>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellingEnrollment, setCancellingEnrollment] = useState(false);
  const [monitoring, setMonitoring] = useState<MonitoringEventsResponse | null>(null);
  const [monitoringCategory, setMonitoringCategory] = useState("");
  const [monitoringResult, setMonitoringResult] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [search, setSearch] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [selected, setSelected] = useState<DeviceDetail | null>(null);
  const [registrations, setRegistrations] = useState<RegistrationSummary[]>([]);
  const [revocationReason, setRevocationReason] = useState("");
  const [revoking, setRevoking] = useState(false);
  const [emailRevocationReason, setEmailRevocationReason] = useState("");
  const [emailRevocationTarget, setEmailRevocationTarget] = useState<RegistrationSummary | null>(null);
  const [revokingEmail, setRevokingEmail] = useState(false);
  const [assignableAdministrators, setAssignableAdministrators] = useState<AssignableAdministrator[]>([]);
  const [assignmentSubject, setAssignmentSubject] = useState("");
  const [assignmentReason, setAssignmentReason] = useState("");
  const [assigning, setAssigning] = useState(false);
  const roles = rolesFromProfile(user.profile as Record<string, unknown>);
  const token = user.access_token;
  const superadmin = isSuperadmin(roles);

  const refresh = useCallback(async () => {
    try {
      setError("");
      const [fleet, devicePage] = await Promise.all([api.fleet(token), api.devices(token)]);
      setReport(fleet);
      setDevices(devicePage.items);
      if (superadmin) setAssignableAdministrators((await api.administrators(token)).items);
      if (canEnroll(roles)) setEnrollments((await api.enrollments(token)).items);
      if (canReadAudit(roles)) setAudit((await api.audit(token)).items);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to load fleet data");
    }
  }, [token, user.profile]);

  useEffect(() => { void refresh(); }, [refresh]);

  const refreshMonitoring = useCallback(async () => {
    try {
      setError("");
      setMonitoring(await api.monitoring(token));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to load operations monitoring");
    }
  }, [token]);

  useEffect(() => {
    if (view !== "monitoring") return;
    void refreshMonitoring();
    const interval = window.setInterval(() => void refreshMonitoring(), 5000);
    return () => window.clearInterval(interval);
  }, [view, refreshMonitoring]);

  const createEnrollment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!enrollmentDraft.displayName.trim()) return;
    try {
      setCreating(true);
      setError("");
      const value = await api.createEnrollment(token, enrollmentRequest(enrollmentDraft));
      setCreated(value);
      setCopyStatus("");
      setEnrollmentDraft(emptyEnrollment);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Enrollment failed");
    } finally {
      setCreating(false);
    }
  };

  const updateEnrollment = (field: keyof EnrollmentDraft, value: string) => {
    setEnrollmentDraft((current) => ({ ...current, [field]: value }));
  };

  const copyBootstrapHandoff = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(bootstrapHandoff(created));
      setCopyStatus("Copied. Move it only through the approved secure channel.");
    } catch {
      setCopyStatus("Clipboard access was blocked. Copy each value manually.");
    }
  };

  const cancelEnrollment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!cancellationTarget || cancellationReason.trim().length < 3) return;
    try {
      setCancellingEnrollment(true);
      setError("");
      await api.cancelEnrollment(token, cancellationTarget.enrollment_id, cancellationReason.trim());
      if (created?.enrollment_id === cancellationTarget.enrollment_id) setCreated(null);
      setCancellationTarget(null);
      setCancellationReason("");
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Enrollment cancellation failed");
    } finally {
      setCancellingEnrollment(false);
    }
  };

  const openDevice = async (deviceId: string) => {
    try {
      setError("");
      const [detail, history] = await Promise.all([api.device(token, deviceId), api.registrations(token, deviceId)]);
      setSelected(detail);
      setRegistrations(history.items);
      setRevocationReason("");
      setEmailRevocationReason("");
      setEmailRevocationTarget(null);
      setAssignmentSubject("");
      setAssignmentReason("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to load device details");
    }
  };

  const revokeSelected = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || revocationReason.trim().length < 3) return;
    try {
      setRevoking(true);
      setError("");
      await api.revoke(token, selected.device_id, revocationReason.trim());
      await refresh();
      await openDevice(selected.device_id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Revocation failed");
    } finally {
      setRevoking(false);
    }
  };

  const revokeEmailSelected = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !emailRevocationTarget || emailRevocationReason.trim().length < 3) return;
    try {
      setRevokingEmail(true);
      setError("");
      await api.revokeRegistration(
        token,
        selected.device_id,
        emailRevocationTarget.registration_id,
        emailRevocationReason.trim(),
      );
      await refresh();
      await openDevice(selected.device_id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Email revocation failed");
    } finally {
      setRevokingEmail(false);
    }
  };

  const assignSelected = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !assignmentSubject || assignmentReason.trim().length < 3) return;
    try {
      setAssigning(true);
      setError("");
      await api.assignDeviceAdmin(token, selected.device_id, assignmentSubject, assignmentReason.trim());
      setAssignmentSubject("");
      setAssignmentReason("");
      await refresh();
      await openDevice(selected.device_id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Device administrator assignment failed");
    } finally {
      setAssigning(false);
    }
  };

  const counts = report?.lifecycle_counts ?? {};
  const visibleDevices = devices.filter((device) => {
    const haystack = `${device.display_name} ${device.device_id}`.toLowerCase();
    return (!search || haystack.includes(search.toLowerCase()))
      && (!lifecycleFilter || device.lifecycle_state === lifecycleFilter);
  });
  const reportTypes = ["fleet-status", "enrollments", "registrations", "message-deliveries", "revocations", "email-revocations", "software-versions", "stale-devices"];
  const monitoringCategories = Array.from(new Set(monitoring?.items.map((item) => item.category) ?? [])).sort();
  const monitoringResults = Array.from(new Set(monitoring?.items.map((item) => item.result) ?? [])).sort();
  const visibleMonitoring = monitoring?.items.filter((item) =>
    (!monitoringCategory || item.category === monitoringCategory)
    && (!monitoringResult || item.result === monitoringResult)
  ) ?? [];
  const administratorBySubject = new Map(assignableAdministrators.map((administrator) => [administrator.subject, administrator]));
  const availableAssignees = assignableAdministrators.filter(
    (administrator) => !selected?.assigned_admin_subjects?.includes(administrator.subject),
  );
  const accessLabel = superadmin ? "Superadmin · all fleet" : "DeviceAdmin · owned or assigned devices";
  return (
    <div className="admin-shell">
      <aside className="sidebar">
        <div className="logo"><span>BT</span><strong>BotTimo</strong></div>
        <nav aria-label="Main navigation">
          {(["dashboard", "fleet", "enrollments", "monitoring", "reports", "audit"] as View[]).map((item) => {
            if (item === "enrollments" && !canEnroll(roles)) return null;
            if (item === "audit" && !canReadAudit(roles)) return null;
            return <button key={item} className={view === item ? "active" : ""} onClick={() => setView(item)}>{item}</button>;
          })}
        </nav>
        <div className="identity">
          <span>{String(user.profile.preferred_username ?? user.profile.email ?? "Administrator")}</span>
          <small>{accessLabel}</small>
          <button onClick={() => userManager.signoutRedirect()}>Sign out</button>
        </div>
      </aside>
      <main className="workspace">
        <header><div><p className="eyebrow">Fleet control</p><h1>{view}</h1></div><button className="secondary" onClick={() => void refresh()}>Refresh</button></header>
        {error && <div className="error-banner">{error}</div>}
        {view === "dashboard" && (
          <>
            <section className="metric-grid">
              <article><span>Total fleet</span><strong>{report?.total_devices ?? "—"}</strong><small>registered assets</small></article>
              <article><span>Active</span><strong>{counts.ACTIVE ?? 0}</strong><small>ready to send</small></article>
              <article><span>Pending</span><strong>{counts.ENROLLMENT_PENDING ?? 0}</strong><small>awaiting bootstrap</small></article>
              <article><span>Messages</span><strong>{report?.total_messages ?? 0}</strong><small>{report?.delivery_counts?.SUBMITTED ?? 0} submitted</small></article>
              <article><span>Attention</span><strong>{report?.recent_failure_count ?? 0}</strong><small>recent failures</small></article>
            </section>
            <section className="content-card"><div className="card-heading"><h2>Latest devices</h2><span>{report?.scope === "ALL" ? "All fleet" : "Owned devices"} · {devices.length} visible</span></div><DeviceTable devices={devices.slice(0, 5)} /></section>
          </>
        )}
        {view === "fleet" && (
          <div className="fleet-layout">
            <section className="content-card">
              <div className="card-heading"><h2>Devices</h2><span>Certificate-backed identities</span></div>
              <div className="filters">
                <input aria-label="Search devices" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name or device ID" />
                <select aria-label="Filter lifecycle" value={lifecycleFilter} onChange={(event) => setLifecycleFilter(event.target.value)}>
                  <option value="">All lifecycle states</option>
                  {["ENROLLMENT_PENDING", "BOOTSTRAPPED", "REGISTRATION_PENDING", "ACTIVE", "REVOKED", "RETIRED"].map((state) => <option key={state}>{state}</option>)}
                </select>
              </div>
              <DeviceTable devices={visibleDevices} onSelect={(deviceId) => void openDevice(deviceId)} />
            </section>
            {selected && (
              <aside className="content-card device-detail" aria-label="Device detail">
                <div className="card-heading"><h2>{selected.display_name}</h2><button className="plain-button" onClick={() => setSelected(null)}>Close</button></div>
                <dl>
                  <dt>Device ID</dt><dd>{selected.device_id}</dd>
                  <dt>Asset ID</dt><dd>{selected.asset_id ?? "—"}</dd>
                  <dt>Site</dt><dd>{selected.site ?? "—"}</dd>
                  <dt>Security owner</dt><dd>{selected.owner_subject}</dd>
                  <dt>Assigned administrators</dt><dd>{selected.assigned_admin_subjects?.length
                    ? selected.assigned_admin_subjects.map((subject) => administratorBySubject.get(subject)?.display_name ?? subject).join(", ")
                    : "—"}</dd>
                  <dt>Owner team</dt><dd>{selected.owner_team ?? "—"}</dd>
                  <dt>Tags</dt><dd>{selected.tags?.join(", ") || "—"}</dd>
                  <dt>Lifecycle</dt><dd><span className="state">{selected.lifecycle_state}</span></dd>
                  <dt>Registration</dt><dd>{selected.registration_state ?? "Not started"}</dd>
                  <dt>Credential</dt><dd>{selected.certificate_status ?? "Not issued"}</dd>
                  <dt>Fingerprint</dt><dd className="fingerprint">{selected.certificate_fingerprint ?? "—"}</dd>
                  <dt>Software</dt><dd>{selected.software_version ?? "unknown"}</dd>
                  <dt>Last seen</dt><dd>{selected.last_seen_at ? new Date(selected.last_seen_at).toLocaleString() : "Never"}</dd>
                  <dt>Created</dt><dd>{new Date(selected.created_at).toLocaleString()}</dd>
                </dl>
                {superadmin && (
                  <form className="warning-zone" onSubmit={assignSelected}>
                    <h3>Assign DeviceAdmin</h3>
                    <p>The security owner remains unchanged. The selected administrator gains scoped access to this device and its records.</p>
                    {availableAssignees.length ? (
                      <>
                        <label>Administrator<select required value={assignmentSubject} onChange={(event) => setAssignmentSubject(event.target.value)}>
                          <option value="">Select a DeviceAdmin</option>
                          {availableAssignees.map((administrator) => <option key={administrator.subject} value={administrator.subject}>{administrator.display_name} · {administrator.email}</option>)}
                        </select></label>
                        <label>Required reason<textarea value={assignmentReason} onChange={(event) => setAssignmentReason(event.target.value)} minLength={3} maxLength={256} required /></label>
                        <button className="secondary" type="submit" disabled={assigning}>{assigning ? "Assigning…" : "Assign administrator"}</button>
                      </>
                    ) : <p className="form-note">All approved DeviceAdmin accounts are already assigned.</p>}
                  </form>
                )}
                <h3>Registration history</h3>
                <ul className="event-list compact">{registrations.length ? registrations.map((item) => <li key={item.registration_id}><div><strong>{item.masked_email}</strong><small>{new Date(item.created_at).toLocaleString()}</small></div><div><span className="state">{item.state}</span>{canRevokeEmail(roles) && ["EMAIL_PENDING", "VERIFIED"].includes(item.state) && <button className="plain-button" onClick={() => { setEmailRevocationTarget(item); setEmailRevocationReason(""); }}>Revoke</button>}</div></li>) : <li className="muted">No registration attempts.</li>}</ul>
                {canRevokeEmail(roles) && emailRevocationTarget && (
                  <form className="warning-zone" onSubmit={revokeEmailSelected}>
                    <h3>Revoke {emailRevocationTarget.masked_email}</h3>
                    <p>This cancels a pending invitation or immediately disables this verified recipient. Other recipients remain available.</p>
                    <label>Required reason<textarea value={emailRevocationReason} onChange={(event) => setEmailRevocationReason(event.target.value)} minLength={3} maxLength={256} required /></label>
                    <button className="secondary" type="submit" disabled={revokingEmail}>{revokingEmail ? "Revoking email…" : "Confirm email revocation"}</button>
                    <button className="plain-button" type="button" onClick={() => setEmailRevocationTarget(null)}>Cancel</button>
                  </form>
                )}
                {selected.registration_state === "REVOKED" && <div className="revoked-note"><strong>Email revoked</strong><p>{selected.email_revocation_reason}</p></div>}
                {canRevoke(roles) && !["REVOKED", "RETIRED"].includes(selected.lifecycle_state) && (
                  <form className="danger-zone" onSubmit={revokeSelected}>
                    <h3>Remove / revoke this device</h3>
                    <p>This immediately blocks backend authorization and queues credential deactivation.</p>
                    <label>Required reason<textarea value={revocationReason} onChange={(event) => setRevocationReason(event.target.value)} minLength={3} maxLength={256} required /></label>
                    <button className="danger" type="submit" disabled={revoking}>{revoking ? "Revoking…" : "Confirm revocation"}</button>
                  </form>
                )}
                {selected.lifecycle_state === "REVOKED" && <div className="revoked-note"><strong>Revoked</strong><p>{selected.revocation_reason}</p></div>}
              </aside>
            )}
          </div>
        )}
        {view === "enrollments" && (
          <div className="enrollment-layout">
            <section className="content-card">
              <div className="card-heading"><h2>Create enrollment</h2><span>15 minute proof</span></div>
              <form onSubmit={createEnrollment}>
                <p className="form-note">The authenticated administrator becomes the immutable security owner. Owner team below is descriptive metadata only.</p>
                <label>Display name<input required value={enrollmentDraft.displayName} onChange={(event) => updateEnrollment("displayName", event.target.value)} maxLength={128} placeholder="Pi workshop" /></label>
                <div className="form-grid">
                  <label>Asset ID<input value={enrollmentDraft.assetId} onChange={(event) => updateEnrollment("assetId", event.target.value)} maxLength={128} placeholder="BT-001" /></label>
                  <label>Site<input value={enrollmentDraft.site} onChange={(event) => updateEnrollment("site", event.target.value)} maxLength={128} placeholder="Brussels workshop" /></label>
                  <label>Owner team<input value={enrollmentDraft.ownerTeam} onChange={(event) => updateEnrollment("ownerTeam", event.target.value)} maxLength={128} placeholder="Raspberry team" /></label>
                  <label>Tags<input value={enrollmentDraft.tags} onChange={(event) => updateEnrollment("tags", event.target.value)} maxLength={256} placeholder="pilot, workshop" /></label>
                </div>
                <label>Notes<textarea value={enrollmentDraft.notes} onChange={(event) => updateEnrollment("notes", event.target.value)} maxLength={1024} placeholder="Physical location or handoff context. Never paste credentials here." /></label>
                <button className="primary" type="submit" disabled={creating}>{creating ? "Creating…" : "Create one-time enrollment"}</button>
              </form>
              {created && (
                <div className="secret-panel" role="region" aria-label="One-time bootstrap handoff">
                  <strong>One-time Raspberry handoff</strong>
                  <p>Copy all three values now. The activation proof cannot be read again.</p>
                  <dl>
                    <dt>Enrollment ID</dt><dd><code>{created.enrollment_id}</code></dd>
                    <dt>Device ID</dt><dd><code>{created.device_id}</code></dd>
                    <dt>Security owner</dt><dd><code>{created.owner_subject}</code></dd>
                    <dt>Activation proof</dt><dd><code>{created.activation_token}</code></dd>
                    <dt>Expires</dt><dd>{new Date(created.expires_at).toLocaleString()}</dd>
                  </dl>
                  <div className="secret-actions">
                    <button className="secondary" type="button" onClick={() => void copyBootstrapHandoff()}>Copy secure handoff JSON</button>
                    <button className="plain-button" type="button" onClick={() => { setCreated(null); setCopyStatus(""); }}>Hide proof</button>
                  </div>
                  {copyStatus && <small>{copyStatus}</small>}
                </div>
              )}
            </section>
            <div className="enrollment-side">
              <section className="content-card first-device-checklist">
                <div className="card-heading"><h2>First device</h2><span>secure sequence</span></div>
                <ol>
                  <li><strong>Create</strong><span>Record the physical asset and generate its one-time proof.</span></li>
                  <li><strong>Transfer</strong><span>Move the enrollment ID, device ID, and proof through an approved secure channel.</span></li>
                  <li><strong>Bootstrap</strong><span>The Pi generates its key locally and submits only a CSR.</span></li>
                  <li><strong>Register email</strong><span>The Pi requests an invitation; the recipient explicitly confirms it.</span></li>
                  <li><strong>Verify</strong><span>Wait for ACTIVE, then run one send and one notify smoke test.</span></li>
                </ol>
              </section>
              <section className="content-card">
                <div className="card-heading"><h2>Recent enrollments</h2><span>{enrollments.length}</span></div>
                <ul className="event-list">{enrollments.map((item) => <li key={item.enrollment_id}><div><strong>{item.device_id}</strong><small>Created {new Date(item.created_at).toLocaleString()} · expires {new Date(item.expires_at).toLocaleString()}{superadmin ? ` · owner ${item.owner_subject}` : ""}</small></div><div><span className="state">{item.state}</span>{item.state === "PENDING" && <button className="plain-button" onClick={() => { setCancellationTarget(item); setCancellationReason(""); }}>Cancel enrollment</button>}</div></li>)}</ul>
                {cancellationTarget && (
                  <form className="danger-zone" onSubmit={cancelEnrollment}>
                    <h3>Cancel enrollment</h3>
                    <p>This permanently invalidates the one-time proof for <strong>{cancellationTarget.device_id}</strong>. The history is retained and the unbootstrapped device record is retired.</p>
                    <label>Required reason<textarea value={cancellationReason} onChange={(event) => setCancellationReason(event.target.value)} minLength={3} maxLength={256} required /></label>
                    <button className="danger" type="submit" disabled={cancellingEnrollment}>{cancellingEnrollment ? "Cancelling…" : "Confirm cancellation"}</button>
                    <button className="plain-button" type="button" onClick={() => { setCancellationTarget(null); setCancellationReason(""); }}>Keep enrollment</button>
                  </form>
                )}
              </section>
            </div>
          </div>
        )}
        {view === "monitoring" && (
          <section className="content-card monitoring-card">
            <div className="card-heading">
              <div><h2>Live operations</h2><small>Safe backend events · refreshes every {monitoring?.poll_after_seconds ?? 5} seconds</small></div>
              <span className="live-status"><i aria-hidden="true" />{monitoring ? "Live" : "Connecting"}</span>
            </div>
            <div className="filters">
              <select aria-label="Filter monitoring category" value={monitoringCategory} onChange={(event) => setMonitoringCategory(event.target.value)}>
                <option value="">All categories</option>
                {monitoringCategories.map((category) => <option key={category}>{category}</option>)}
              </select>
              <select aria-label="Filter monitoring result" value={monitoringResult} onChange={(event) => setMonitoringResult(event.target.value)}>
                <option value="">All results</option>
                {monitoringResults.map((result) => <option key={result}>{result}</option>)}
              </select>
              <button className="secondary" onClick={() => void refreshMonitoring()}>Refresh now</button>
            </div>
            <p className="form-note">{monitoring?.scope === "ALL" ? "All fleet" : "Owned devices only"} · last refreshed {monitoring ? new Date(monitoring.generated_at).toLocaleTimeString() : "—"}. Sensitive request data and credentials are never shown.</p>
            <div className="monitoring-table" role="region" aria-label="Recent operational events">
              {visibleMonitoring.length ? <table><thead><tr><th>Time</th><th>Category</th><th>Action</th><th>Result</th><th>Target</th><th>Correlation</th></tr></thead><tbody>{visibleMonitoring.map((item) => <tr key={item.event_id}><td>{new Date(item.occurred_at).toLocaleTimeString()}</td><td><span className="state">{item.category}</span></td><td>{item.action.replaceAll("_", " ")}</td><td><span className={`result result-${item.result.toLowerCase()}`}>{item.result}</span></td><td><strong>{item.device_id ?? item.target_id}</strong><small>{item.target_type}</small></td><td><code>{item.correlation_id}</code></td></tr>)}</tbody></table> : <div className="empty-state">No matching operational events yet.</div>}
            </div>
          </section>
        )}
        {view === "reports" && (
          <section className="content-card">
            <div className="card-heading"><h2>Fleet reports</h2><span>{report?.scope === "ALL" ? "All fleet" : "Owned devices only"}</span></div>
            <div className="report-grid">
              {reportTypes.map((reportType) => <article key={reportType}><strong>{reportType.replaceAll("-", " ")}</strong><p>Server-generated CSV for {superadmin ? "the full fleet" : "devices you own"}, with minimized identifiers and no credential material.</p>{canExport(roles) ? <button className="secondary" onClick={() => void api.exportReport(token, reportType).catch((reason) => setError(reason instanceof Error ? reason.message : "Export failed"))}>Export CSV</button> : <small>Export is not available for this account.</small>}</article>)}
            </div>
          </section>
        )}
        {view === "audit" && <section className="content-card"><div className="card-heading"><h2>Audit trail</h2><span>append-only</span></div><ul className="event-list">{audit.map((item) => <li key={item.audit_event_id}><div><strong>{item.action.replaceAll("_", " ")}</strong><small>{item.actor_subject} · {new Date(item.occurred_at).toLocaleString()}</small></div><span className="state">{item.result}</span></li>)}</ul></section>}
      </main>
    </div>
  );
}

function DeviceTable({ devices, onSelect }: { devices: DeviceSummary[]; onSelect?: (deviceId: string) => void }) {
  if (!devices.length) return <div className="empty-state">No devices enrolled yet.</div>;
  return <div className="table-wrap"><table><thead><tr><th>Device</th><th>State</th><th>Software</th><th>Last seen</th>{onSelect && <th><span className="visually-hidden">Actions</span></th>}</tr></thead><tbody>{devices.map((device) => <tr key={device.device_id}><td><strong>{device.display_name}</strong><small>{device.device_id}</small></td><td><span className="state">{device.lifecycle_state}</span></td><td>{device.software_version ?? "—"}</td><td>{device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : "Never"}</td>{onSelect && <td><button className="plain-button" onClick={() => onSelect(device.device_id)}>View</button></td>}</tr>)}</tbody></table></div>;
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (window.location.pathname === "/confirm") return;
    const userLoaded = (current: User) => setUser(current);
    const userUnloaded = () => setUser(null);
    const sessionExpired = () => setUser(null);
    userManager.events.addUserLoaded(userLoaded);
    userManager.events.addUserUnloaded(userUnloaded);
    userManager.events.addAccessTokenExpired(sessionExpired);
    const complete = async () => {
      try {
        if (window.location.pathname === "/auth/callback" && window.location.search.includes("code=")) {
          await userManager.signinRedirectCallback();
          window.history.replaceState({}, "", "/");
        }
        setUser(await userManager.getUser());
      } finally {
        setLoading(false);
      }
    };
    void complete();
    return () => {
      userManager.events.removeUserLoaded(userLoaded);
      userManager.events.removeUserUnloaded(userUnloaded);
      userManager.events.removeAccessTokenExpired(sessionExpired);
    };
  }, []);

  if (window.location.pathname === "/confirm") return <ConfirmationPage />;
  if (loading) return <main className="loading-screen">Loading BotTimo…</main>;
  return user && !user.expired ? <Admin user={user} /> : <Login />;
}
